import * as THREE from "three";

const BANK_AMOUNT = 0.18;
const TARGET_DECAY_LAMBDA = 6;
const CURRENT_FOLLOW_LAMBDA = 14;
// Slower follow for the sustained roll so it eases in/out of corners
// instead of snapping. ~0.4s settle time at this lambda.
const SUSTAIN_FOLLOW_LAMBDA = 5;

// Adds a transient camera roll on Z when the world rotates so the cinematic
// "leans into the turn". The bank impulse pushes a target angle, which
// decays to zero over time; the camera's actual roll lerps toward that
// target. Apply post-SpringArm and pre-CameraShake so the spring's
// look-at quaternion isn't fought.
//
// On top of the per-rotate transient, BankingFx layers a *sustained*
// roll via setSustainedRoll(rad). The sustained roll has its own
// LERP-followed inner state so it eases in/out of the corner instead
// of snapping; the apply() output is `transient + sustainedCurrent`.
export class CameraBank {
  private current = 0;
  private target = 0;
  private sustainedTarget = 0;
  private sustainedCurrent = 0;

  bank(direction: 1 | -1, magnitude = 1): void {
    this.target += direction * BANK_AMOUNT * magnitude;
  }

  // BankingFx writes here per frame: a desired sustained roll in
  // radians (roughly +/- 0.18 rad ~= 10deg for a normal banking
  // segment). Pass 0 to lerp back to neutral. Cheap; the actual
  // smoothing happens in apply().
  setSustainedRoll(radians: number): void {
    this.sustainedTarget = radians;
  }

  reset(): void {
    this.current = 0;
    this.target = 0;
    this.sustainedTarget = 0;
    this.sustainedCurrent = 0;
  }

  apply(camera: THREE.PerspectiveCamera, dt: number): void {
    const decay = 1 - Math.exp(-TARGET_DECAY_LAMBDA * dt);
    this.target = THREE.MathUtils.lerp(this.target, 0, decay);
    const follow = 1 - Math.exp(-CURRENT_FOLLOW_LAMBDA * dt);
    this.current = THREE.MathUtils.lerp(this.current, this.target, follow);

    const sustainedFollow = 1 - Math.exp(-SUSTAIN_FOLLOW_LAMBDA * dt);
    this.sustainedCurrent = THREE.MathUtils.lerp(
      this.sustainedCurrent,
      this.sustainedTarget,
      sustainedFollow
    );

    const total = this.current + this.sustainedCurrent;
    if (Math.abs(total) > 1e-5) {
      camera.rotateZ(total);
    }
  }
}
